import { ApiError, type PosCheckoutResult, queryKeys } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { Reader, Terminal } from '@stripe/terminal-js'
import { loadStripeTerminal } from '@stripe/terminal-js'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { getStripe } from '../lib/stripe.js'

export interface PendingCheckout {
  checkoutId: string
  /** Set only for a newly entered card or a reader — null for a saved-card
   *  attempt, which settles (or declines) synchronously in the initial
   *  `POST /checkout` call, before this dialog ever needs to mount Stripe.js. */
  clientSecret: string | null
  publishableKey: string | null
  method: 'new_card' | 'reader' | 'saved_card'
}

const RECORDABLE = ['cash', 'external_card', 'check', 'zelle'] as const
type RecordableProvider = (typeof RECORDABLE)[number]

/**
 * Finishes a card-mode combined checkout (t0943) — a newly entered card or a
 * connected reader still needs confirming against the PaymentIntent
 * `POST /checkout` opened; a declined saved card lands here too, with
 * nothing left to confirm, only a choice of what to do next. Either way,
 * staff can always fall back to a tender collected another way, or cancel
 * the sale outright — both unwind cleanly since nothing has been charged.
 *
 * Mirrors `RecordPaymentDialog`'s card panels (`record-rental-payment-dialog.tsx`)
 * almost exactly; kept as its own component because it settles a checkout,
 * not a single rental payment.
 */
export function CheckoutPaymentDialog({
  pending,
  onSettled,
  onCancelled,
}: {
  pending: PendingCheckout | null
  onSettled: (result: PosCheckoutResult) => void
  onCancelled: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [switching, setSwitching] = useState(false)
  const [tenderProvider, setTenderProvider] = useState<RecordableProvider>('cash')

  useEffect(() => {
    if (pending) setSwitching(false)
  }, [pending])

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.units.all() }),
    ])

  const cancel = useMutation({
    mutationFn: () => api.post(`/checkout/${pending?.checkoutId}/cancel`, {}),
    onSuccess: async () => {
      await invalidate()
      toast.success('Sale cancelled — nothing was charged')
      onCancelled()
    },
    onError: (error) =>
      toast.error('Could not cancel', error instanceof ApiError ? error.message : undefined),
  })

  const tender = useMutation({
    mutationFn: () =>
      api.post<PosCheckoutResult>(`/checkout/${pending?.checkoutId}/tender`, {
        provider: tenderProvider,
        reference: null,
      }),
    onSuccess: async (result) => {
      await invalidate()
      onSettled(result)
    },
    onError: (error) =>
      toast.error(
        'Could not record the payment',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!pending) return null

  const declined = pending.clientSecret === null

  return (
    <Dialog open onOpenChange={(next) => !next && cancel.mutate()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{declined ? 'Card declined' : 'Take payment'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {declined ? (
            <p className="text-sm text-foreground-muted">
              The sale and any rental are already on file, unpaid — record how it was actually paid,
              or cancel the sale.
            </p>
          ) : pending.method === 'new_card' ? (
            <NewCardPanel
              checkoutId={pending.checkoutId}
              clientSecret={pending.clientSecret as string}
              publishableKey={pending.publishableKey as string}
              onSettled={async (result) => {
                await invalidate()
                onSettled(result)
              }}
            />
          ) : (
            <ReaderPanel
              checkoutId={pending.checkoutId}
              clientSecret={pending.clientSecret as string}
              onSettled={async (result) => {
                await invalidate()
                onSettled(result)
              }}
            />
          )}

          {switching || declined ? (
            <Field label="Recorded as">
              {(props) => (
                <Select
                  value={tenderProvider}
                  onValueChange={(v) => setTenderProvider(v as RecordableProvider)}
                >
                  <SelectTrigger {...props}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="external_card">Card machine</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}>
            Cancel sale
          </Button>
          {!switching && !declined ? (
            <Button variant="secondary" onClick={() => setSwitching(true)}>
              Use another method
            </Button>
          ) : null}
          {switching || declined ? (
            <Button variant="primary" loading={tender.isPending} onClick={() => tender.mutate()}>
              Record payment
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Mounts Stripe.js against the checkout's client secret — same split as
 *  `NewCardPanel`/`NewCardForm` in `record-rental-payment-dialog.tsx`. */
function NewCardPanel({
  checkoutId,
  clientSecret,
  publishableKey,
  onSettled,
}: {
  checkoutId: string
  clientSecret: string
  publishableKey: string
  onSettled: (result: PosCheckoutResult) => void
}) {
  const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey])

  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <NewCardForm checkoutId={checkoutId} onSettled={onSettled} />
    </Elements>
  )
}

function NewCardForm({
  checkoutId,
  onSettled,
}: {
  checkoutId: string
  onSettled: (result: PosCheckoutResult) => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [isWorking, setIsWorking] = useState(false)

  const settle = useMutation({
    mutationFn: () => api.post<PosCheckoutResult>(`/checkout/${checkoutId}/settle`, {}),
    onSuccess: onSettled,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not confirm the charge'),
  })

  const handleCharge = async () => {
    if (!stripe || !elements) return
    setError(null)
    setIsWorking(true)
    try {
      const result = await stripe.confirmPayment({ elements, redirect: 'if_required' })
      if (result.error) {
        setError(result.error.message ?? 'The card was not accepted. Please try again.')
        return
      }
      await settle.mutateAsync()
    } catch {
      // settle's onError already recorded the message in `error` state.
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="w-full"
        disabled={!stripe || !elements || isWorking}
        loading={isWorking}
        onClick={() => void handleCharge()}
      >
        {error ? 'Try again' : 'Charge card'}
      </Button>
    </div>
  )
}

/** A connected physical reader — same flow as `ReaderPanel` in
 *  `record-rental-payment-dialog.tsx`, settling a checkout instead of a
 *  single rental payment. */
function ReaderPanel({
  checkoutId,
  clientSecret,
  onSettled,
}: {
  checkoutId: string
  clientSecret: string
  onSettled: (result: PosCheckoutResult) => void
}) {
  const [terminal, setTerminal] = useState<Terminal | null>(null)
  const [readers, setReaders] = useState<Reader[]>([])
  const [connected, setConnected] = useState<Reader | null>(null)
  const [status, setStatus] = useState('Looking for a reader…')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function setup() {
      const StripeTerminal = await loadStripeTerminal()
      if (!StripeTerminal || cancelled) return
      const t = StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const { secret } = await api.post<{ secret: string }>(
            '/rentals/terminal/connection-token',
            {},
          )
          return secret
        },
        onUnexpectedReaderDisconnect: () => {
          if (!cancelled) {
            setConnected(null)
            setStatus('The reader disconnected — reconnect to try again.')
          }
        },
      })
      if (cancelled) return
      setTerminal(t)

      const result = await t.discoverReaders({ simulated: import.meta.env.DEV })
      if (cancelled) return
      if ('error' in result) {
        setError(result.error.message)
        return
      }
      setReaders(result.discoveredReaders)
      setStatus(
        result.discoveredReaders.length
          ? 'Choose a reader to connect.'
          : 'No readers found nearby.',
      )
    }
    void setup()
    return () => {
      cancelled = true
    }
  }, [])

  const connect = async (reader: Reader) => {
    if (!terminal) return
    setError(null)
    setStatus(`Connecting to ${reader.label ?? reader.id}…`)
    const result = await terminal.connectReader(reader)
    if ('error' in result) {
      setError(result.error.message)
      return
    }
    setConnected(result.reader)
    setStatus('Reader connected — insert, tap, or swipe the card.')
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: fires once per connection, against the one client secret this dialog was opened with
  useEffect(() => {
    if (!terminal || !connected) return
    const t = terminal
    let cancelled = false
    async function collect() {
      setStatus('Insert, tap, or swipe the card…')
      const collected = await t.collectPaymentMethod(clientSecret)
      if (cancelled) return
      if ('error' in collected) {
        setError(collected.error.message)
        return
      }
      setStatus('Processing…')
      const processed = await t.processPayment(collected.paymentIntent)
      if (cancelled) return
      if ('error' in processed) {
        setError(processed.error.message)
        return
      }
      try {
        const result = await api.post<PosCheckoutResult>(`/checkout/${checkoutId}/settle`, {})
        if (!cancelled) onSettled(result)
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : 'Could not confirm the charge')
      }
    }
    void collect()
    return () => {
      cancelled = true
    }
  }, [connected])

  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground-muted">{status}</p>
      {error ? (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {!connected && readers.length ? (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {readers.map((reader) => (
            <li key={reader.id} className="flex items-center justify-between px-3 py-2 text-sm">
              <span>{reader.label ?? reader.id}</span>
              <Button size="sm" variant="secondary" onClick={() => void connect(reader)}>
                Connect
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
